/**
 * Story figures painted as separate cutouts (scripts/art/figures.py) and stood on a scene's painted layer,
 * for scenes whose reused plate lacks the character a target names. Keyed by story scene id, so a plate
 * shared by two scenes only shows the figure in the scene that needs it.
 */
import type { Uv } from './anchors';

export interface FigureDef {
  id: string;
  /** story element this figure embodies (its anchor points at the head) */
  element: string;
  /** plate uv of the point between the feet */
  feet: Uv;
  /** figure height as a fraction of the plate height */
  height: number;
  /** painted layer it stands on (0 backdrop .. 3 nearest) */
  layer: number;
  /** cutout width / height */
  aspect: number;
}

const BASE = '/art/generated/figures/';

export const FIGURES: Record<string, FigureDef[]> = {
  s10_tide_lock: [{ id: 'pell_tide', element: 'scribe', feet: [0.6, 0.87], height: 0.42, layer: 2, aspect: 548 / 900 }],
  s17_dam_burst: [{ id: 'pell_dam', element: 'scribe', feet: [0.44, 0.95], height: 0.45, layer: 3, aspect: 644 / 900 }],
  s15_bard_reprise: [{ id: 'mira_stair', element: 'hero', feet: [0.425, 0.985], height: 0.48, layer: 2, aspect: 432 / 900 }],
};

export function figuresFor(sceneId: string): FigureDef[] {
  return FIGURES[sceneId] ?? [];
}

export function figureUrl(f: FigureDef): string {
  return `${BASE}${f.id}.webp`;
}

/** Head position of a figure in plate uv, for anchors and pins. */
export function figureHead(f: FigureDef): Uv {
  return [f.feet[0], f.feet[1] - f.height * 0.88];
}

/** Anchor override when a figure embodies the element. */
export function figureAnchor(sceneId: string, elementId: string): { uv: Uv; layer: number } | undefined {
  const f = figuresFor(sceneId).find((x) => x.element === elementId);
  return f ? { uv: figureHead(f), layer: f.layer } : undefined;
}
